import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { X } from 'lucide-react';
import { countdownParts } from '@repo/shared-types';
import { Button } from '@repo/ui/src/components/ui/button';
import { useSubscription } from '../../../hooks/useSubscription';
import { useCan } from '../../../hooks/useCan';

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

/**
 * THI-200 follow-up — the in-app "invitation" counterpart to NudgeBanner's
 * "warning": an active offer is good news, so it never shares a frame or a
 * colour with the amber nudge (brand tint here, always). Mounted in
 * DashboardLayout ABOVE NudgeBanner.
 *
 * Shown whenever `useSubscription().data.offer` is present and the viewer
 * can manage billing — independent of the nudge machinery, which only
 * covers renewal/trial/downgrade heads-up. Dismiss is remembered in
 * localStorage keyed by the promotion id (unlike the landing bar's
 * session-only dismiss: this is a signed-in org with a stable identity to
 * remember against) — but a dismissal is overridden once 3 days or fewer
 * remain, so the banner cannot be silenced right up to the deadline.
 */
export function OfferBanner() {
  const { t } = useTranslation('billing');
  const { data } = useSubscription();
  const canManageBilling = useCan('manage:settings');
  const offer = canManageBilling ? (data?.offer ?? null) : null;

  const [clock, setClock] = useState(() => new Date());
  useEffect(() => {
    if (!offer) return;
    const id = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(id);
  }, [offer]);

  const dismissKey = offer ? `offer-dismissed:${offer.promotionId}` : '';
  const [dismissed, setDismissed] = useState(
    () => !!offer && typeof window !== 'undefined' && localStorage.getItem(dismissKey) === '1',
  );

  if (!offer) return null;

  const parts = countdownParts(offer.deadline, clock);
  // The dismissal is a "stop nagging me" preference, not a "hide the
  // deadline" one — once the offer is genuinely about to lapse (≤3 days) it
  // shows again regardless of what localStorage remembers.
  if (dismissed && parts.days >= 3) return null;

  const clockStr = `${pad2(parts.hours)}:${pad2(parts.minutes)}:${pad2(parts.seconds)}`;
  const offerCountdown =
    parts.days > 0 ? t('offer.countdown.days', { days: parts.days, clock: clockStr }) : t('offer.countdown.today', { clock: clockStr });

  // Merge with the trial's own days-left when the org is TRIALING and the
  // trial nudge would otherwise have shown (same 7-day window) — NudgeBanner
  // suppresses its own TRIALING frame in exactly this case, so the trial
  // countdown has to live somewhere, and it lives here.
  const trialDaysLeft = data?.status === 'TRIALING' && data.nudge?.kind === 'TRIALING' ? data.nudge.daysLeft : null;
  const countdownText =
    trialDaysLeft !== null ? t('offer.countdown.trialMerge', { trialDays: trialDaysLeft, offerCountdown }) : offerCountdown;

  const headline = offer.headline || t('offer.banner.fallback', { name: offer.name, pct: offer.percentOff });

  return (
    <div className="relative flex min-h-[44px] flex-col justify-center gap-[6px] border-b border-brand-200 bg-brand-50 px-[16px] py-[8px] pr-[44px] text-brand-800 sm:flex-row sm:items-center sm:justify-between sm:gap-[16px] sm:py-0 sm:pr-[16px]">
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-[10px] gap-y-[2px]">
        <span className="shrink-0 font-mono text-[11px] uppercase tracking-[0.14em] text-brand-600">
          {t('offer.banner.label')}
        </span>
        <span className="text-[13px]">{headline}</span>
      </div>
      {/* Phone: the chip wraps to as many lines as it needs and the CTA sits
          under it; desktop: chip + CTA + dismiss in one row. The dismiss is
          pinned top-right on phone (44px right padding above reserves it). */}
      <div className="flex shrink-0 flex-wrap items-center gap-[8px] sm:flex-nowrap sm:justify-end sm:gap-[12px]">
        <span className="inline-flex min-h-[24px] items-center rounded-[9px] border border-brand-200 bg-surface-0 px-[8px] py-[2px] font-mono text-[12px] leading-[1.35] tabular-nums text-brand-800 sm:whitespace-nowrap">
          {countdownText}
        </span>
        {/* Johnny (2026-09-06): the CTA goes to the billing page, not the
            compare dialog — the page shows the plan chooser with the offer
            lines in context and reads better than a modal over whatever the
            teacher was doing. */}
        <Button asChild size="sm">
          <Link to="/settings/billing">{t('offer.banner.cta')}</Link>
        </Button>
        <button
          type="button"
          onClick={() => {
            localStorage.setItem(dismissKey, '1');
            setDismissed(true);
          }}
          className="absolute right-[12px] top-[10px] opacity-60 hover:opacity-100 sm:static sm:right-auto sm:top-auto"
          aria-label={t('offer.banner.dismiss')}
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
